/** Valid WGS84 coordinates — rejects placeholders like 1234 / 5678. */
export function isValidCoord(lat: number, lon: number): boolean {
  if (lat == null || lon == null || Number.isNaN(lat) || Number.isNaN(lon)) return false;
  if (lat === 0 && lon === 0) return false;
  return lat >= -90 && lat <= 90 && lon >= -180 && lon <= 180;
}

function toCoordinateNumber(value: unknown): number | null {
  if (value == null || value === '') return null;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  const s = String(value).trim().replace(/,/g, '.');
  const n = Number.parseFloat(s);
  return Number.isFinite(n) ? n : null;
}

export type OrderLocationCoords = { lat: number; long: number };

/** Read lat/long from API order_location or saved address shapes. */
export function parseOrderLocation(orderLocation: unknown): OrderLocationCoords | null {
  if (orderLocation == null) return null;

  if (typeof orderLocation === 'string') {
    const parts = orderLocation.split(',').map((p) => p.trim());
    if (parts.length >= 2) {
      const lat = toCoordinateNumber(parts[0]);
      const lng = toCoordinateNumber(parts[1]);
      if (lat != null && lng != null && isValidCoord(lat, lng)) {
        return { lat, long: lng };
      }
    }
    return null;
  }

  if (typeof orderLocation !== 'object') return null;
  const o = orderLocation as Record<string, unknown>;

  const tryPair = (latKey: string, lngKey: string): OrderLocationCoords | null => {
    const lat = toCoordinateNumber(o[latKey]);
    const lng = toCoordinateNumber(o[lngKey]);
    if (lat != null && lng != null && isValidCoord(lat, lng)) {
      return { lat, long: lng };
    }
    return null;
  };

  return (
    tryPair('lat', 'long') ||
    tryPair('lat', 'lng') ||
    tryPair('latitude', 'longitude') ||
    null
  );
}

/** Extract coordinates from a saved address (flat or nested `location`). */
export function resolveAddressCoordinates(address: unknown): { lat: string; long: string } | null {
  if (!address || typeof address !== 'object') return null;
  const a = address as Record<string, unknown>;
  const nested = a.location;
  const sources: unknown[] = [a];
  if (nested && typeof nested === 'object') sources.push(nested);

  for (const source of sources) {
    const parsed = parseOrderLocation(source);
    if (parsed) {
      return { lat: String(parsed.lat), long: String(parsed.long) };
    }
  }
  return null;
}

export function formatOrderLocationDisplay(orderLocation: unknown): string {
  const parsed = parseOrderLocation(orderLocation);
  if (!parsed) return 'Not available';
  return `${parsed.lat.toFixed(6)}, ${parsed.long.toFixed(6)}`;
}

export function googleMapsUrlForOrderLocation(orderLocation: unknown): string | null {
  const parsed = parseOrderLocation(orderLocation);
  if (!parsed) return null;
  const q = encodeURIComponent(`${parsed.lat},${parsed.long}`);
  return `https://www.google.com/maps/search/?api=1&query=${q}`;
}

/** City name normalization for assign-SP (Bhadravati / Bhadravathi, Bengaluru / Bangalore, etc.). */
export function normalizeCityKey(city: string | null | undefined): string {
  return String(city ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .replace(/[.,]/g, '');
}

const CITY_ALIAS_GROUPS: string[][] = [
  ['bangalore', 'bengaluru', 'bangaluru'],
  ['shimoga', 'shivamogga', 'shivamoga'],
  ['mysore', 'mysuru'],
  ['mangalore', 'mangaluru'],
  ['hubli', 'hubballi'],
  ['belgaum', 'belagavi'],
  ['bhadravati', 'bhadravathi'],
];

function cityKeysEquivalent(a: string, b: string): boolean {
  if (a === b) return true;
  for (const group of CITY_ALIAS_GROUPS) {
    if (group.includes(a) && group.includes(b)) return true;
  }
  return false;
}

export function citiesMatch(
  a: string | null | undefined,
  b: string | null | undefined,
): boolean {
  const ka = normalizeCityKey(a);
  const kb = normalizeCityKey(b);
  if (!ka || !kb) return false;
  return cityKeysEquivalent(ka, kb);
}

export function captainInOrderArea(
  captainActiveCity: string | null | undefined,
  orderCity: string | null | undefined,
): boolean {
  const active = String(captainActiveCity ?? '').trim();
  const order = String(orderCity ?? '').trim();
  if (!active || !order) return false;
  return citiesMatch(active, order);
}

function isCityMismatchBlockReason(reason: string | null | undefined): boolean {
  const r = String(reason ?? '');
  return /working in/i.test(r) && /order is in/i.test(r);
}

export function resolveAdminCanAssign(sp: {
  can_assign?: boolean;
  assign_block_reason?: string | null;
  captain_active_city?: string | null;
  order_service_city?: string | null;
  captain_work_enabled?: boolean | null;
  work_status_label?: string | null;
}): boolean {
  if (sp.can_assign === true) return true;
  if (sp.captain_work_enabled === false) return false;
  if (String(sp.work_status_label ?? '').toLowerCase() === 'offline') return false;

  if (
    sp.can_assign === false &&
    isCityMismatchBlockReason(sp.assign_block_reason) &&
    captainInOrderArea(sp.captain_active_city, sp.order_service_city)
  ) {
    return true;
  }

  return sp.can_assign !== false;
}

export function fixCaptainAssignEligibility<T extends {
  can_assign?: boolean;
  assign_block_reason?: string | null;
  assign_warning?: string | null;
  captain_active_city?: string | null;
  order_service_city?: string | null;
  captain_work_enabled?: boolean | null;
  work_status_label?: string | null;
}>(sp: T): T {
  if (!resolveAdminCanAssign(sp)) return sp;
  if (sp.can_assign === true) return sp;
  return {
    ...sp,
    can_assign: true,
    assign_block_reason: null,
    assign_warning:
      sp.assign_warning ??
      'Area matched (city spelling). Deploy latest ots_be on EC2 if assignment API still fails.',
  };
}
